/* eslint-disable react/no-danger */

import React, { useEffect, useState } from "react";

import { useFormik } from "formik";

import ClayButton from "@clayui/button";
import ClayForm, { ClayCheckbox, ClayInput } from "@clayui/form";
import ClayIcon from "@clayui/icon";
import ClayModal, { useModal } from "@clayui/modal";

import * as flags from "country-flag-icons/react/3x2";

import * as Yup from "yup";

import CountrySelect from "./components/CountrySelect";

import { translate } from "./i18n";
import ApiClient from "./utils/ApiClient";
import Liferay from "./utils/Liferay";

/**
 * Componente principal de registro de médico.
 *
 * @returns Component react.
 */
function App() {

  // ======================
  // Estados del componente
  // ======================

  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [spinner, setSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptMktNotifications, setAcceptMktNotifications] = useState(false);
  const [termsAndConditionsContent, setTermsAndConditionsContent] = useState("");
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [isCloseModal, setIsCloseModal] = useState(false);

  // =======================
  // Hook para uso del modal
  // =======================

  const { observer, onOpenChange, open } = useModal();

  // =====================
  // Expresiones regulares
  // =====================

  const REGEXP = {
    ALPHABETICAL: /^[-'a-zA-ZÀ-ÖØ-öø-ÿ\s]+$/,
    NUMERIC: /^\d+$/,
    LENGTH: /^\d{10,15}$/,
    PASSWORD: /^(?=.*[A-Z])(?=.*\d)(?=.*[¡!¿?-_+.:,;"@#$%/^&*()[\]{}])(?=.*[a-z]).{8,}$/,
  };

  // ====================
  // Definicion de Formik
  // ====================

  const formik = useFormik({
    initialValues: {
      names: "",
      firstSurname: "",
      secondSurname: "",
      email: "",
      countryCode: "",
      phoneNumber: "",
      password: "",
      passwordConfirmation: "",
      isProfessional: false,
      termsAndConditionsRead: false,
    },
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      names: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          translate("dr-now-doctor-sign-up-invalid-characters"),
        )
        .required(translate("dr-now-doctor-sign-up-field-required")),
      firstSurname: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          translate("dr-now-doctor-sign-up-invalid-characters"),
        )
        .required(translate("dr-now-doctor-sign-up-field-required")),
      secondSurname: Yup.string()
        .matches(
          REGEXP.ALPHABETICAL,
          translate("dr-now-doctor-sign-up-invalid-characters"),
        ),
      email: Yup.string()
        .email(translate("dr-now-doctor-sign-up-invalid-email"))
        .required(translate("dr-now-doctor-sign-up-field-required")),
      countryCode: Yup.string().required(),
      phoneNumber: Yup.string()
        .matches(
          REGEXP.NUMERIC,
          translate("dr-now-doctor-sign-up-invalid-phone-number"),
        )
        .matches(
          REGEXP.LENGTH,
          translate("dr-now-doctor-sign-up-invalid-phone-number"),
        )
        .required(translate("dr-now-doctor-sign-up-field-required")),
      password: Yup.string()
        .matches(
          REGEXP.PASSWORD,
          translate("dr-now-doctor-sign-up-invalid-password"),
        )
        .required(translate("dr-now-doctor-sign-up-field-required")),
      passwordConfirmation: Yup.string()
        .oneOf(
          [Yup.ref("password"), null],
          translate("dr-now-doctor-sign-up-passwords-must-match"),
        )
        .required(translate("dr-now-doctor-sign-up-field-required")),
      isProfessional: Yup.boolean().oneOf([true]),
      termsAndConditionsRead: Yup.boolean().oneOf([true]),
    }),
    onSubmit: async (values) => {

      const countryCode = String(values.countryCode).split(" ")[1] || values.countryCode;

      try {

        setSpinner(true);

        const checkEmail = await ApiClient.checkEmail(values.email);

        if (checkEmail) {

          throw String("EMAIL_ALREADY_REGISTERED");

        } else {

          const user = {
            givenName: values.names,
            familyName: values.firstSurname,
            additionalName: values.secondSurname,
            password: values.password,
            emailAddress: values.email,
            currentPassword: values.password,
            languageId: Liferay.ThemeDisplay.getLanguageId(),
            status: "Active",
            userAccountContactInformation: {
              telephones: [
                {
                  phoneNumber: `${countryCode}-${values.phoneNumber}`,
                  primary: true,
                },
              ],
            },
            drNowDoctor: [{
              name: `${values.names} ${values.firstSurname} ${values.secondSurname}`,
              email: values.email,
            }],
          };

          await ApiClient.createUser(user);

          formik.resetForm();

          formik.setValues({
            names: "",
            firstSurname: "",
            secondSurname: "",
            email: "",
            phoneNumber: "",
            password: "",
            passwordConfirmation: "",
            isProfessional: false,
            termsAndConditionsRead: false,
          });

          setAcceptMktNotifications(false);

          setIsCloseModal(true);
          onOpenChange(true);

        }

      } catch (error) {

        if (error === "EMAIL_ALREADY_REGISTERED") {

          Liferay.Util.openToast({
            title: "",
            message: `
              <h4 class='font-weight-medium m-0'>
                ${translate("dr-now-doctor-sign-up-email-registered-title")}
              </h4>
              <span class='text-3'>
                ${translate("dr-now-doctor-sign-up-email-registered-message")}
              </span>
            `,
            type: "danger",
            toastProps: {
              hideCloseIcon: true,
            },
          });

        } else {

          Liferay.Util.openToast({
            title: "",
            message: `
              <h4 class='font-weight-medium m-0'>
                ${translate("dr-now-doctor-sign-up-service-error")}
              </h4>
              <span class='text-3'>
                ${translate("dr-now-doctor-sign-up-please-try-again-later")}
              </span>
            `,
            type: "danger",
            toastProps: {
              hideCloseIcon: true,
            },
          });

        }

      } finally {

        setSpinner(false);

      }

    },
  });

  // ========================
  // Funciones del componente
  // ========================

  /**
   * Obtiene la clase a aplicar al elemento del formulario.
   *
   * @param field - Campo a validar.
   *
   * @returns Clase CSS.
   */
  function getFormControlClassName(field) {

    if (!formik.getFieldMeta(field).touched) {

      return "";

    }

    return formik.getFieldMeta(field)?.error ? "has-error" : "is-valid";

  }

  /**
   * Obtiene los catálogos.
   */
  async function getCatalogs() {

    try {

      setLoading(true);

      const response = await ApiClient.getCountriesCatalog();

      const countriesWithoutFlags = response.data?.items;

      const countriesWithFlags = countriesWithoutFlags.map((country) => ({
        ...country,
        flag: flags[country.a2],
      }));

      setCountries(countriesWithFlags);

    } catch {

      Liferay.Util.openToast({
        title: "",
        message: `
          <h4 class='font-weight-medium m-0'>
            ${translate("dr-now-doctor-sign-up-service-error")}
          </h4>
          <span class='text-3'>
            ${translate("dr-now-doctor-sign-up-please-try-again-later")}
          </span>
        `,
        type: "danger",
        toastProps: {
          hideCloseIcon: true,
        },
      });

    } finally {

      setLoading(false);

    }

  }

  /**
   * Obtiene el contenido del modal de términos y condiciones.
   */
  async function getTermsAndConditionsContent() {

    try {

      const response = await ApiClient.getTermsAndConditionsContent();

      setTermsAndConditionsContent(
        response.data,
      );

    } catch (error) {

      Liferay.Util.openToast({
        title: "",
        message: `
          <h4 class='font-weight-medium m-0'>
            ${translate("dr-now-doctor-sign-up-service-error")}
          </h4>
          <span class='text-3'>
            ${translate("dr-now-doctor-sign-up-please-try-again-later")}
          </span>
          `,
        type: "danger",
        toastProps: {
          hideCloseIcon: true,
        },
      });

    }

  }

  /**
   * Valida si el formulario es válido.
   *
   * @returns True si el formulario es válido, de lo contrario False.
   */
  function isFormValid() {

    return (
      formik.isValid
      && !formik.errors.names
      && !formik.errors.firstSurname
      && !formik.errors.email
      && !formik.errors.countryCode
      && !formik.errors.phoneNumber
      && !formik.errors.password
      && !formik.errors.passwordConfirmation
      && formik.values.isProfessional
      && formik.values.termsAndConditionsRead
    );

  }

  /**
   * Carga la estructura del modal perfil completado.
   *
   * @returns Estructura del modal perfil completado.
   */
  function renderModal() {

    if (!open || !isCloseModal) {

      return false;

    }

    return (
      <ClayModal center disableAutoClose observer={observer} size="lg">

        <ClayModal.Header withTitle={false}>

          <div className="modal-title text-center text-5 font-weight-semi-bold">
            <p>{translate("register-completed")}</p>
          </div>

        </ClayModal.Header>

        <ClayModal.Body>

          <div className="row">

            <div className="col-auto">

              <p className="text-4 font-weight-light text-center">
                {translate("register-completed-message-1")}
                <br />
                {translate("register-completed-message-2")}
                <br />
                {translate("register-completed-message-3")}
              </p>

            </div>

          </div>

          <div className="row justify-content-center">

            <div className="col-auto">

              <button
                className="btn btn-primary btn-lg"
                type="button"
                onClick={() => {

                  onOpenChange(false);

                  Liferay.SPA.app.navigate("/web/dr-now-doctor-public/home");

                }}
              >
                {translate("go-home")}
              </button>

            </div>
          </div>

        </ClayModal.Body>

      </ClayModal>
    );

  }

  /**
   * Carga la estructura del modal de términos y condiciones.
   *
   * @returns Estructura del modal de términos y condiciones.
   */
  function renderTermsAndConditionsModal() {

    if (!open || isCloseModal) {

      return false;

    }

    return (

      <ClayModal
        center
        disableAutoClose
        observer={observer}
        size="full-screen"
        className="modal-height-lg"
      >

        <ClayModal.Header className="text-right" withTitle={false}>

          <button
            aria-label="terms"
            type="button"
            className="close"
            onClick={() => onOpenChange(false)}
          >

            <ClayIcon
              spritemap={Liferay.Icons.spritemap}
              symbol="dr-now-times"
            />

          </button>

        </ClayModal.Header>

        <ClayModal.Body>

          {termsAndConditionsContent && (
            <>
              <div className="row mb-4 text-center">
                <div
                  className="col"
                  dangerouslySetInnerHTML={{
                    __html: termsAndConditionsContent,
                  }}
                />
              </div>

              <div className="row justify-content-center">
                <div className="col-auto">

                  <ClayButton
                    displayType="primary"
                    size="lg"
                    block
                    onClick={() => {

                      formik.setFieldValue("termsAndConditionsRead", true);
                      onOpenChange(false);

                    }}
                  >
                    {translate("dr-now-doctor-sign-up-accept")}
                  </ClayButton>

                </div>
              </div>

            </>
          )}

        </ClayModal.Body>

      </ClayModal>
    );

  }

  /**
   * Método que muestra el mensaje de error de un campo.
   *
   * @param fieldName - Nombre del campo.
   *
   * @returns Componente con el mensaje de error del campo.
   */
  function renderFieldErrorMessage(fieldName) {

    if (formik.getFieldMeta(fieldName).touched && formik.errors?.[fieldName]) {

      return (
        <div className="form-feedback-item">

          <ClayIcon
            className="text-2 text-danger mr-1"
            spritemap={Liferay.Icons.spritemap}
            symbol="dr-now-bell"
          />

          {formik.errors[fieldName]}
        </div>
      );

    }

    return null;

  }

  /**
   * Renderiza el formulario de datos del médico.
   *
   * @returns Component con Formulario.
   */
  function renderDoctorForm() {

    return (
      <>

        <div className="row">
          <div className="col">

            <ClayForm.Group className={getFormControlClassName("names")}>

              <label className="form-control-label" htmlFor="names">

                {translate("dr-now-doctor-sign-up-names")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="names"
                name="names"
                value={formik.values.names}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate("dr-now-doctor-sign-up-names-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("names")}

                <div className="form-text">
                  {translate("dr-now-doctor-sign-up-enter-two-or-more-names")}
                </div>

              </div>

            </ClayForm.Group>

          </div>
        </div>

        <div className="row">
          <div className="col">

            <ClayForm.Group className={getFormControlClassName("firstSurname")}>

              <label className="form-control-label" htmlFor="firstSurname">

                {translate("dr-now-doctor-sign-up-first-surname")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="firstSurname"
                name="firstSurname"
                value={formik.values.firstSurname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate(
                  "dr-now-doctor-sign-up-surname-placeholder",
                )}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("firstSurname")}

                <div className="form-text">
                  {translate("dr-now-doctor-sign-up-enter-your-first-surname")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

          <div className="col">

            <ClayForm.Group className={getFormControlClassName("secondSurname")}>

              <label className="form-control-label" htmlFor="secondSurname">

                {translate("dr-now-doctor-sign-up-second-surname")}

              </label>

              <ClayInput
                id="secondSurname"
                name="secondSurname"
                value={formik.values.secondSurname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate("dr-now-doctor-sign-up-surname-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("secondSurname")}

                <div className="form-text">
                  {translate("dr-now-doctor-sign-up-enter-your-second-surname")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

        </div>

        <div className="row">
          <div className="col-6">

            <ClayForm.Group className={getFormControlClassName("email")}>

              <label className="form-control-label" htmlFor="email">

                {translate("dr-now-doctor-sign-up-email")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate("dr-now-doctor-sign-up-email-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("email")}

                <div className="form-text">
                  {translate("dr-now-doctor-sign-up-enter-your-work-email")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

          <div className="col-2">

            <ClayForm.Group className={getFormControlClassName("countryCode")}>

              <label className="form-control-label" htmlFor="countryCode">

                {translate("dr-now-doctor-sign-up-country-code")}

                <span className="text-danger">*</span>

              </label>

              <CountrySelect formik={formik} countries={countries} loading={loading} />

              <div className="form-feedback-group">

                <div className="form-text">
                  {translate("dr-now-doctor-sign-up-country")}
                </div>

              </div>

            </ClayForm.Group>

          </div>

          <div className="col">

            <ClayForm.Group className={getFormControlClassName("phoneNumber")}>

              <label className="form-control-label" htmlFor="phoneNumber">

                {translate("dr-now-doctor-sign-up-phone-number")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput
                id="phoneNumber"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={translate("dr-now-doctor-sign-up-phone-number-placeholder")}
                autoComplete="off"
                type="text"
              />

              <div className="form-feedback-group">

                {renderFieldErrorMessage("phoneNumber")}

                <div className="form-text">
                  {translate("dr-now-doctor-sign-up-enter-your-phone-number")}
                </div>

              </div>

            </ClayForm.Group>

          </div>
        </div>

      </>
    );

  }

  /**
   * Renderiza el formulario de contraseña.
   *
   * @returns Componente React.
   */
  function renderPasswordForm() {

    return (
      <>
        <div className="row mb-3">
          <div className="col text-center">

            <span className="text-5 font-weight-semi-bold text-primary">
              {translate("dr-now-doctor-sign-up-password")}
            </span>

          </div>
        </div>

        <div className="row mb-3">
          <div className="col text-center">

            <span className="text-dark">
              {translate("dr-now-doctor-sign-up-enter-a-safe-password-msg")}
            </span>

          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-8">

            <ClayForm.Group className={getFormControlClassName("password")}>

              <label className="form-control-label" htmlFor="password">

                {translate("dr-now-doctor-sign-up-password")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput.Group>

                <ClayInput.GroupItem prepend>

                  <ClayInput
                    id="password"
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={translate("dr-now-doctor-sign-up-password-placeholder")}
                    autoComplete="off"
                    type={showPassword ? "text" : "password"}
                  />

                </ClayInput.GroupItem>

                <ClayInput.GroupItem append shrink>

                  <ClayButton
                    aria-label="showPassword"
                    className="px-3 py-2"
                    displayType="unstyled"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >

                    <ClayIcon
                      className="text-primary text-4"
                      symbol={showPassword ? "dr-now-hide" : "dr-now-show"}
                      spritemap={Liferay.Icons.spritemap}
                    />

                  </ClayButton>

                </ClayInput.GroupItem>

              </ClayInput.Group>

              <div className="form-feedback-group">

                {renderFieldErrorMessage("password")}

                <div className="form-text">
                  {`* ${translate("dr-now-doctor-sign-up-at-least-eight-characters")}`}
                </div>

                <div className="form-text mt-0">
                  {`* ${translate("dr-now-doctor-sign-up-at-least-one-capital-letter")}`}
                </div>

                <div className="form-text mt-0">
                  {`* ${translate("dr-now-doctor-sign-up-at-least-one-number")}`}
                </div>

                <div className="form-text mt-0">
                  {`* ${translate("dr-now-doctor-sign-up-at-least-one-special-character")}`}
                </div>

              </div>

            </ClayForm.Group>

          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-8">

            <ClayForm.Group className={getFormControlClassName("passwordConfirmation")}>

              <label
                className="form-control-label"
                htmlFor="passwordConfirmation"
              >

                {translate("dr-now-doctor-sign-up-password-confirmation")}

                <span className="text-danger">*</span>

              </label>

              <ClayInput.Group>

                <ClayInput.GroupItem prepend>

                  <ClayInput
                    id="passwordConfirmation"
                    name="passwordConfirmation"
                    value={formik.values.passwordConfirmation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={
                      translate("dr-now-doctor-sign-up-password-confirmation-placeholder")
                    }
                    autoComplete="off"
                    type={showPasswordConfirmation ? "text" : "password"}
                  />

                </ClayInput.GroupItem>

                <ClayInput.GroupItem append shrink>

                  <ClayButton
                    aria-label="passwordConfirmation"
                    className="px-3 py-2"
                    displayType="unstyled"
                    type="button"
                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                  >

                    <ClayIcon
                      className="text-primary text-4"
                      symbol={showPasswordConfirmation ? "dr-now-hide" : "dr-now-show"}
                      spritemap={Liferay.Icons.spritemap}
                    />

                  </ClayButton>

                </ClayInput.GroupItem>

              </ClayInput.Group>

              <div className="form-feedback-group">

                {renderFieldErrorMessage("passwordConfirmation")}

                <div className="form-text">
                  {translate("dr-now-doctor-sign-up-password-confirmation-msg")}
                </div>

              </div>

            </ClayForm.Group>

          </div>
        </div>
      </>
    );

  }

  /**
   * Renderiza los checkboxes de la sección de términos y condiciones.
   *
   * @returns Componente React.
   */
  function renderCheckboxesSection() {

    return (
      <>

        <div className="row">
          <div className="col">

            <ClayCheckbox
              id="isProfessional"
              name="isProfessional"
              checked={formik.values.isProfessional}
              value={formik.values.isProfessional}
              onChange={formik.handleChange}
              label={
                `${translate("dr-now-doctor-sign-up-health-professional-disclaimer-agree")}*`
              }
            />

          </div>
        </div>

        <div className="row">
          <div className="col">

            <ClayCheckbox
              id="termsAndConditionsRead"
              name="termsAndConditionsRead"
              checked={formik.values.termsAndConditionsRead}
              value={formik.values.termsAndConditionsRead}
              onChange={formik.handleChange}
              inline
              label={(
                <span>
                  {`${translate("dr-now-doctor-sign-up-read-and-accept")}`}
                </span>
              )}
            />

            <button
              type="button"
              className="ml-1 mb-2 p-0 btn btn-link text-2 font-weight-bold"
              onClick={() => {

                setIsCloseModal(false);
                onOpenChange(true);

              }}
            >

              {`${translate("dr-now-doctor-sign-up-terms-and-conditions")}*`}

            </button>

          </div>
        </div>

        <div className="row">
          <div className="col">

            <ClayCheckbox
              id="acceptMktNotifications"
              name="acceptMktNotifications"
              checked={acceptMktNotifications}
              onChange={() => setAcceptMktNotifications(!acceptMktNotifications)}
              label={(
                <span>

                  {translate("dr-now-doctor-sign-up-marketing-notifications-agree")}

                  <span className="text-muted">
                    {` (${translate("dr-now-doctor-sign-up-optional")})`}
                  </span>

                </span>
              )}

            />

          </div>
        </div>

      </>
    );

  }

  /**
   * Renderiza la sección de botones.
   *
   * @returns Componente React.
   */
  function renderButtonsSection() {

    return (
      <>

        <div className="row mb-2">
          <div className="col text-center">

            <span className="text-2">
              {`*${translate("dr-now-doctor-sign-up-previous-fields-are-mandatory")}`}
            </span>

          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-auto">

            <ClayButton
              displayType="primary"
              type="submit"
              size="lg"
              disabled={!isFormValid() || spinner || loading}
              block
            >
              {!spinner ? translate("dr-now-doctor-sign-up-continue")
                : (
                  <>

                    <span className="mr-2">
                      {translate("dr-now-doctor-sign-up-processing")}
                    </span>

                    <ClayIcon
                      className="spinning-animation"
                      spritemap={Liferay.Icons.spritemap}
                      symbol="reload"
                    />

                  </>
                )}
            </ClayButton>

          </div>
        </div>

      </>
    );

  }

  // ===============================
  // Función de carga del componente
  // ===============================

  useEffect(() => {

    if (!Liferay || !Liferay.SPA) {

      return;

    }

    getTermsAndConditionsContent();

    getCatalogs();

  }, []);

  // =======================
  // Template del componente
  // =======================

  return (
    <>

      <div className="row mb-2">
        <div className="col text-center">

          <h3 className="font-weight-semi-bold text-primary">
            {translate("dr-now-doctor-sign-up")}
          </h3>

        </div>
      </div>

      <div className="row mb-2">
        <div className="col text-center font-weight-light">

          <p className="text-dark">
            <span className="font-weight-semi-bold">
              {translate("dr-now-doctor-sign-up-welcome-message")}
            </span>
            {" "}
            {translate("dr-now-doctor-sign-up-welcome-please-fill-your-data")}
          </p>

        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>

        <div className="row mb-4">
          <div className="col">{renderDoctorForm()}</div>
        </div>

        <div className="row mb-4">
          <div className="col">{renderPasswordForm()}</div>
        </div>

        <hr />

        <div className="row mb-4">
          <div className="col">{renderCheckboxesSection()}</div>
        </div>

        <hr />

        <div className="row mb-4">
          <div className="col">{renderButtonsSection()}</div>
        </div>

      </form>

      {renderTermsAndConditionsModal()}

      {renderModal()}

    </>
  );

}

export default App;
